
import { Form } from "@/components/ui/form";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { DiaryEntry } from "@/utils/diary/types";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import DialogFooterActions from "./DialogFooterActions";
import EntryTextFields from "./EntryTextFields";
import EntryImagesSection from "./EntryImagesSection";
import EntryMetadataFields from "./EntryMetadataFields";
import { useAuth } from "@/contexts/auth";
import { saveImageToGallery, ensureImageInGallery } from "@/utils/entryEditor";
import { useLanguage } from "@/contexts/language";

export const entryFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  entryText: z.string().min(1, "Entry text is required"),
  height: z.string().optional(),
  weight: z.string().optional(),
  diaperChanges: z.string().optional(),
  milkIntake: z.string().optional(),
  milestone: z.string().optional(),
});

export type EntryFormValues = z.infer<typeof entryFormSchema>;

interface EntryEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  entry: DiaryEntry;
  onSave: (updatedEntry: DiaryEntry) => void;
}

const EntryEditDialog = ({ 
  isOpen, 
  onOpenChange, 
  entry,
  onSave 
}: EntryEditDialogProps) => {
  const [images, setImages] = useState<string[]>(entry.images || []);
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const form = useForm<EntryFormValues>({
    resolver: zodResolver(entryFormSchema),
    defaultValues: {
      title: entry.title || `Today with ${entry.babyName}`,
      entryText: entry.entryText,
      height: entry.height || "",
      weight: entry.weight || "",
      diaperChanges: entry.diaperChanges || "",
      milkIntake: entry.milkIntake || "",
      milestone: entry.milestone || entry.milestones?.[0] || "",
    },
  });

  useEffect(() => {
    form.reset({
      title: entry.title || `Today with ${entry.babyName}`,
      entryText: entry.entryText,
      height: entry.height || "",
      weight: entry.weight || "",
      diaperChanges: entry.diaperChanges || "",
      milkIntake: entry.milkIntake || "",
      milestone: entry.milestone || entry.milestones?.[0] || "",
    });
    setImages(entry.images || []);
  }, [entry, form]);

  const handleSave = async (values: EntryFormValues) => {
    // Check if there are new images compared to the original entry
    const originalImages = entry.images || [];
    const newImages = images.filter(img => !originalImages.includes(img));
    
    // Save any new images to the gallery
    if (user?.id && newImages.length > 0) {
      for (const imageUrl of newImages) {
        try {
          await saveImageToGallery(imageUrl, user.id, language);
        } catch (error) {
          console.error("Failed to save image to gallery (non-critical):", error);
          // Continue even if gallery save fails
        }
      }
    }
    
    // Also make sure all existing images are in the gallery (for backwards compatibility)
    if (user?.id && images.length > 0) {
      for (const imageUrl of images) {
        try {
          await ensureImageInGallery(imageUrl, user.id);
        } catch (error) {
          console.error("Failed to ensure image in gallery (non-critical):", error);
        }
      }
    }
    
    const updatedEntry: DiaryEntry = {
      ...entry,
      title: values.title,
      entryText: values.entryText,
      height: values.height || undefined,
      weight: values.weight || undefined,
      diaperChanges: values.diaperChanges || undefined,
      milkIntake: values.milkIntake || undefined,
      milestone: values.milestone || undefined,
      milestones: values.milestone ? [values.milestone] : undefined,
      images: images.length > 0 ? images : undefined,
    };
    onSave(updatedEntry);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Diary Entry</DialogTitle>
          <DialogDescription>
            Make changes to your diary entry. Click save when you're done.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
            <EntryTextFields control={form.control} />
            
            <EntryImagesSection 
              images={images} 
              setImages={setImages} 
            />
            
            <EntryMetadataFields control={form.control} />
            
            <DialogFooterActions onCancel={() => onOpenChange(false)} />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EntryEditDialog;
